import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Users, Settings, CreditCard, Activity, Trash2, Crown, Plus, Minus, Check, Target } from "lucide-react";
import Layout from "@/components/Layout";
import { AdminSignalQuality } from "@/components/AdminSignalQuality";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-7 h-7 text-cyan-400" />
          <div><h1 className="font-display text-3xl font-bold tracking-tight">Admin Control Panel</h1>
            <p className="text-slate-400 text-sm">Full control over users, plans, payments and AI usage.</p></div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="admin-tab-overview"><Activity className="w-4 h-4 mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1.5" />Users</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings"><Settings className="w-4 h-4 mr-1.5" />Plans & Trial</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments"><CreditCard className="w-4 h-4 mr-1.5" />Payments</TabsTrigger>
            <TabsTrigger value="usage" data-testid="admin-tab-usage"><Activity className="w-4 h-4 mr-1.5" />AI Usage</TabsTrigger>
            <TabsTrigger value="quality" data-testid="admin-tab-quality"><Target className="w-4 h-4 mr-1.5" />Signal Quality</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
          <TabsContent value="payments"><PaymentsTab /></TabsContent>
          <TabsContent value="usage"><UsageTab /></TabsContent>
          <TabsContent value="quality"><AdminSignalQuality /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const cards = [
    { label: "Total Users", value: stats?.total_users, color: "text-white" },
    { label: "On Trial", value: stats?.on_trial, color: "text-amber-400" },
    { label: "Active Subscriptions", value: stats?.active_subscriptions, color: "text-emerald-400" },
    { label: "Expired", value: stats?.expired, color: "text-red-400" },
    { label: "AI Analyses Run", value: stats?.total_ai_analyses, color: "text-cyan-400" },
    { label: "Revenue (USD)", value: `$${stats?.revenue ?? 0}`, color: "text-emerald-400" },
    { label: "Pending Payments", value: stats?.pending_payments, color: "text-amber-400" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.label} className="glass rounded-xl p-5">
          <p className="text-xs uppercase tracking-wider text-slate-400">{c.label}</p>
          <p className={`font-mono-num text-3xl font-bold mt-1 ${c.color}`}>{c.value ?? "—"}</p>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const action = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Access</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3"><p className="font-medium">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-cyan-500/15 text-cyan-400" : "bg-white/10 text-slate-300"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs ${u.access?.active ? "text-emerald-400" : "text-red-400"}`}>
                    {u.access?.reason}{u.access?.reason === "trial" ? ` (${u.access.days_left}d)` : ""}
                  </span>
                </td>
                <td className="px-4 py-3"><span className={u.status === "suspended" ? "text-red-400" : "text-slate-300"}>{u.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <IconBtn title="Extend trial +7d" testId={`extend-trial-${u.user_id}`}
                      onClick={() => action(() => api.patch(`/admin/users/${u.user_id}`, { extend_trial_days: 7 }), "Trial extended")}><Plus className="w-4 h-4" /></IconBtn>
                    <IconBtn title="Grant monthly sub" testId={`grant-sub-${u.user_id}`}
                      onClick={() => action(() => api.post(`/admin/users/${u.user_id}/grant-subscription`, { plan_id: "monthly" }), "Subscription granted")}><Crown className="w-4 h-4 text-emerald-400" /></IconBtn>
                    <IconBtn title={u.status === "suspended" ? "Activate" : "Suspend"} testId={`toggle-status-${u.user_id}`}
                      onClick={() => action(() => api.patch(`/admin/users/${u.user_id}`, { status: u.status === "suspended" ? "active" : "suspended" }), "Status updated")}><Minus className="w-4 h-4 text-amber-400" /></IconBtn>
                    <IconBtn title="Delete" testId={`delete-user-${u.user_id}`}
                      onClick={() => action(() => api.delete(`/admin/users/${u.user_id}`), "User deleted")}><Trash2 className="w-4 h-4 text-red-400" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.put("/admin/settings", { trial_days: parseInt(s.trial_days), plans: s.plans, offer: s.offer, disclaimer: s.disclaimer,
        ai_costs: { scan_usd: parseFloat(s.ai_costs?.scan_usd ?? 0.02), chat_usd: parseFloat(s.ai_costs?.chat_usd ?? 0.01), voice_usd: parseFloat(s.ai_costs?.voice_usd ?? 0.005), daily_soft_limit: parseInt(s.ai_costs?.daily_soft_limit ?? 150) } });
      toast.success("Settings saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (!s) return null;

  const updPlan = (i, k, v) => { const plans = [...s.plans]; plans[i] = { ...plans[i], [k]: k === "price" ? v : v }; setS({ ...s, plans }); };

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div className="glass rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Trial Configuration</h3>
        <div className="flex items-center gap-3 max-w-xs">
          <label className="text-sm text-slate-400">Trial length (days)</label>
          <Input type="number" value={s.trial_days} onChange={(e) => setS({ ...s, trial_days: e.target.value })}
            className="bg-white/5 border-white/10 font-mono-num w-24" data-testid="trial-days-input" />
        </div>
      </div>

      <div className="glass rounded-2xl p-6" data-testid="admin-ai-costs">
        <h3 className="font-semibold mb-1">AI Usage Rates (Credit Meter)</h3>
        <p className="text-xs text-slate-500 mb-4">Estimated cost shown to traders per AI call, and the daily soft limit that pauses live scanning.</p>
        <div className="grid sm:grid-cols-4 gap-4">
          {[["scan_usd", "$ per chart scan", 0.02, "0.001"], ["chat_usd", "$ per broker chat", 0.01, "0.001"], ["voice_usd", "$ per voice call", 0.005, "0.001"], ["daily_soft_limit", "Daily scan limit", 150, "1"]].map(([k, label, def_, step]) => (
            <div key={k}>
              <label className="text-xs text-slate-400 block mb-1">{label}</label>
              <Input type="number" step={step} value={s.ai_costs?.[k] ?? def_} onChange={(e) => setS({ ...s, ai_costs: { ...(s.ai_costs || {}), [k]: e.target.value } })}
                className="bg-white/5 border-white/10 font-mono-num" data-testid={`ai-cost-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Subscription Plans & Pricing</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {s.plans.map((p, i) => (
            <div key={p.id} className="border border-white/10 rounded-xl p-4 space-y-2">
              <p className="font-sub font-bold">{p.name}</p>
              <label className="text-xs text-slate-400">Price (USD)</label>
              <Input type="number" value={p.price} onChange={(e) => updPlan(i, "price", parseFloat(e.target.value))}
                className="bg-white/5 border-white/10 font-mono-num" data-testid={`plan-price-${p.id}`} />
              <label className="text-xs text-slate-400">Interval label</label>
              <Input value={p.interval} onChange={(e) => updPlan(i, "interval", e.target.value)} className="bg-white/5 border-white/10" />
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Promotional Offer</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={!!s.offer?.active} onChange={(e) => setS({ ...s, offer: { ...s.offer, active: e.target.checked } })} data-testid="offer-active-checkbox" />
            <label className="text-sm text-slate-400">Offer active</label>
          </div>
          <Input value={s.offer?.label || ""} onChange={(e) => setS({ ...s, offer: { ...s.offer, label: e.target.value } })} placeholder="Label" className="bg-white/5 border-white/10" />
          <Input value={s.offer?.text || ""} onChange={(e) => setS({ ...s, offer: { ...s.offer, text: e.target.value } })} placeholder="Offer text" className="bg-white/5 border-white/10" />
          <Input value={s.offer?.code || ""} onChange={(e) => setS({ ...s, offer: { ...s.offer, code: e.target.value } })} placeholder="Promo code" className="bg-white/5 border-white/10 font-mono-num" />
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Legal Disclaimer</h3>
        <Textarea value={s.disclaimer} onChange={(e) => setS({ ...s, disclaimer: e.target.value })} rows={4} className="bg-white/5 border-white/10" data-testid="disclaimer-textarea" />
      </div>

      <Button onClick={save} data-testid="save-settings-button" className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold">
        <Check className="w-4 h-4 mr-2" /> Save All Settings
      </Button>
    </div>
  );
}

function PaymentsTab() {
  const [txns, setTxns] = useState([]);
  const load = () => api.get("/admin/payments").then(({ data }) => setTxns(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const confirm = async (id) => {
    try { await api.post(`/admin/payments/${id}/confirm`); toast.success("Payment confirmed & subscription activated"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="glass rounded-2xl overflow-hidden">
      {txns.length === 0 ? <div className="p-12 text-center text-slate-500">No transactions yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-payments-table">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">Plan</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Action</th></tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.txn_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3">{t.user_email}</td>
                  <td className="px-4 py-3">{t.plan_name}</td>
                  <td className="px-4 py-3 text-right font-mono-num">${t.amount}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{t.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {t.status === "pending" && <Button size="sm" onClick={() => confirm(t.txn_id)} data-testid={`confirm-payment-${t.txn_id}`} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] h-8">Confirm</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsageTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/ai-usage").then(({ data }) => setData(data)).catch(() => {}); }, []);
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-usage">
      <div className="glass rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Top AI Users</h3>
        <ul className="space-y-2">
          {(data?.top_users || []).filter((u) => u.ai_usage_count > 0).map((u) => (
            <li key={u.user_id} className="flex items-center justify-between text-sm">
              <span className="text-slate-300">{u.name} <span className="text-slate-500">· {u.email}</span></span>
              <span className="font-mono-num text-emerald-400 font-bold">{u.ai_usage_count}</span>
            </li>
          ))}
          {!(data?.top_users || []).some((u) => u.ai_usage_count > 0) && <li className="text-slate-500 text-sm">No AI usage recorded yet.</li>}
        </ul>
      </div>
      <div className="glass rounded-2xl p-6">
        <h3 className="font-semibold mb-4">Recent Analyses</h3>
        <ul className="space-y-2 max-h-96 overflow-y-auto">
          {(data?.recent_analyses || []).map((a) => (
            <li key={a.analysis_id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
              <span className="font-mono-num">{a.symbol || "—"}</span>
              <span className={`text-xs ${a.result?.recommendation === "BUY" ? "text-emerald-400" : a.result?.recommendation === "SELL" ? "text-red-400" : "text-amber-400"}`}>{a.result?.recommendation}</span>
              <span className="text-slate-500 text-xs">{new Date(a.created_at).toLocaleDateString()}</span>
            </li>
          ))}
          {!(data?.recent_analyses || []).length && <li className="text-slate-500 text-sm">No analyses yet.</li>}
        </ul>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testId }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId}
      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-slate-400 transition-colors">
      {children}
    </button>
  );
}
